import { fmtDate, fmtMonth, num, todayISO } from '../calc/format.js';
import { buildSeries, periodBounds, previousPeriod, rangePreset, yearAgoPeriod } from '../calc/periods.js';
import { customerValue } from '../calc/customers.js';
import { linePredicate, narrowReceipt } from '../calc/selection.js';
import { computeStats } from '../calc/stats.js';
import { clockProblem, clockWindow, describeDayTime, receiptPredicate, selectedDays } from '../calc/timefilter.js';
import { el } from './dom.js';
import { renderAll } from './main.js';
import { syncQuickFilter } from './quickfilter.js';
import { syncMenuSummaries } from './sidebar.js';
import { state } from './state.js';
import { writeHash } from './urlstate.js';

/* --------------------------------------------------------------------------
   Filter
   -------------------------------------------------------------------------- */

/** Värdet i ett flerval, eller tom lista innan väljarna är skapade. */
const msValue = ms => ms ? ms.value : [];

export function currentFilter(){
  const mode = document.querySelector('input[name=tf]:checked').value;
  return {
    mode,
    date:  el('fDate').value,
    month: el('fMonth').value,
    from:  el('fFrom').value,
    to:    el('fTo').value,
    weekdays: [...document.querySelectorAll('input.wd:checked')].map(c => Number(c.value)),
    timeFrom: el('fClockFrom').value,
    timeTo:   el('fClockTo').value,
    stores:    msValue(state.scopeSelects.store),
    registers: msValue(state.scopeSelects.register),
    cashiers:  msValue(state.scopeSelects.cashier),
    customers: msValue(state.scopeSelects.customer),
    groups:    msValue(state.lineSelects.group),
    suppliers: msValue(state.lineSelects.supplier)
  };
}

/**
 * Utvecklingsvyns serie. Den följer urvalet av butik, kassa, kassör,
 * veckodagar och klockslag. Normalt visar den hela filens tidsspann, eftersom
 * poängen är att se förloppet; med "Vald period" bara den valda perioden,
 * till exempel tio veckors helger.
 */
export function rebuildSeries(f = currentFilter()){
  const days = selectedDays(f.weekdays);
  if(state.trendSpan === 'period'){
    const b = periodBounds(f, state.bounds);
    state.series = b ? buildSeries(state.view, state.grain, b, days) : [];
  }else{
    state.series = buildSeries(state.scoped, state.grain, state.bounds, days);
  }
}

export function applyFilter(){
  const f = currentFilter();

  // Urvalsfilter först: jämförelseperioden ska ha samma butik, kassa,
  // kassör, veckodagar, klockslag, artikelgrupp och leverantör
  let scoped = state.receipts;
  const among = (list, get) => { const set = new Set(list); scoped = scoped.filter(r => set.has(String(get(r)))); };
  if(f.stores.length)    among(f.stores,    r => r.store);
  if(f.registers.length) among(f.registers, r => r.register);
  if(f.cashiers.length)  among(f.cashiers,  r => r.cashier);
  if(f.customers.length) among(f.customers, customerValue);
  const when = receiptPredicate(f);
  if(when) scoped = scoped.filter(when);

  const keep = linePredicate(f);
  state.lineFiltered = !!keep;
  if(keep){
    scoped = scoped.map(r => narrowReceipt(r, keep)).filter(r => r.lines.length);
  }
  el('lineNote').hidden = !keep;

  let list = scoped;

  if(f.mode === 'today'){
    const t = todayISO();
    list = list.filter(r => r.day === t);
  }else if(f.mode === 'date' && f.date){
    list = list.filter(r => r.day === f.date);
  }else if(f.mode === 'month' && f.month){
    list = list.filter(r => r.month === f.month);
  }else if(f.mode === 'range'){
    // Tål omvänd ordning på från/till
    let a = f.from, b = f.to;
    if(a && b && a > b){ [a,b] = [b,a]; }
    if(a) list = list.filter(r => r.day >= a);
    if(b) list = list.filter(r => r.day <= b);
  }

  state.view  = list;
  state.stats = computeStats(list);

  // Jämförelse: närmast föregående period eller samma period föregående år
  const mode = state.compareMode;
  const prev = mode === 'yoy' ? yearAgoPeriod(f, scoped, state.receipts) : previousPeriod(f, scoped, state.receipts);
  state.prev = (prev && prev.list.length)
    ? {stats: computeStats(prev.list), label: prev.label, partial: !!prev.partial,
       basis: prev.basis, dayCounts: prev.dayCounts || null}
    : null;

  state.scoped = scoped;
  state.dayTime = describeDayTime(f);
  state.weekdays = f.weekdays;
  rebuildSeries(f);

  renderTags(f);
  syncDayTime(f);
  syncPreset(f);
  renderAll();
  writeHash();
}

/** Förklaringen under veckodagar och klockslag, och knappen som nollar dem. */
function syncDayTime(f){
  const problem = clockProblem(f.timeFrom, f.timeTo);
  const win = clockWindow(f.timeFrom, f.timeTo);
  const active = !!(selectedDays(f.weekdays) || win);
  let hint = 'Inga dagar valda betyder alla dagar.';
  if(problem) hint = problem;
  else if(win && win.wraps) hint = 'Över midnatt: kvitton efter midnatt räknas till den dag de slogs in.';
  else if(active) hint = 'Gäller alla flikar och jämförelsen. Välj Per vecka under Utveckling för att se en summa per vecka.';
  el('dayTimeHint').textContent = hint;
  el('btnDayTimeClear').hidden = !active && !f.timeFrom && !f.timeTo;
}

/** Snabbvalet visar det val som stämmer med intervallet, annars ingenting.
    Två snabbval kan ge samma intervall — "hittills i år" och "hela filen" i
    en fil från i år. Då står det kvar som kunden valde. */
function syncPreset(f){
  const sel = el('fPreset');
  const matches = key => {
    const r = key && rangePreset(key, state.bounds);
    return !!r && r.from === f.from && r.to === f.to;
  };
  let match = '';
  if(f.mode === 'range' && state.bounds){
    match = matches(sel.value) ? sel.value
      : ([...sel.options].find(o => !o.disabled && matches(o.value))?.value || '');
  }
  sel.value = match;
}

export function renderTags(f){
  let t = 'Hela perioden';
  if(f.mode === 'today')      t = 'Idag, ' + fmtDate(todayISO());
  else if(f.mode === 'date')  t = f.date ? fmtDate(f.date) : 'Datum';
  else if(f.mode === 'month') t = f.month ? fmtMonth(f.month) : 'Månad';
  else if(f.mode === 'range'){
    let a = f.from, b = f.to;
    if(a && b && a > b) [a,b] = [b,a];
    t = (a ? fmtDate(a) : 'start') + ' – ' + (b ? fmtDate(b) : 'slut');
  }
  el('tagTime').textContent = t;
  syncQuickFilter();

  el('tagDayTime').hidden = !state.dayTime;
  el('tagDayTime').textContent = state.dayTime;

  const sel = state.scopeSelects;
  const parts = [];
  // Butiker nämns bara när filen har fler än en
  if(state.multiStore) parts.push(f.stores.length ? sel.store.describe() : 'Alla butiker');
  if(f.registers.length) parts.push(sel.register.describe().toLowerCase());
  if(f.cashiers.length)  parts.push(sel.cashier.describe());
  // Kunden är en egen del i menyn, men en del av urvalet i taggarna
  const where = parts.join(' · ');
  const customer = f.customers.length ? sel.customer.describe() : '';
  const scope = [where, customer].filter(Boolean).join(' · ');
  el('tagScope').textContent = scope.charAt(0).toUpperCase() + scope.slice(1);
  el('tagScope').hidden = !scope;

  const lineParts = [];
  if(f.groups.length)    lineParts.push(state.lineSelects.group.describe());
  if(f.suppliers.length) lineParts.push(state.lineSelects.supplier.describe());
  el('tagLines').hidden = !lineParts.length;
  el('tagLines').textContent = lineParts.join(' · ');

  const n = state.view.length;
  el('tagCount').textContent = n === 1 ? '1 kvitto' : num(n) + ' kvitton';
  syncMenuSummaries({urval: where.charAt(0).toUpperCase() + where.slice(1), kund: customer});
}
